// AI usage entitlements — the arithmetic and thresholds around a plan allowance.
//
// MOVED HERE from @weconsultrx/claude-synthesis (2026-08-27). Nothing in this
// file changed; only its address did. It is pure arithmetic with zero imports,
// and it now sits next to `./catalog.ts`, which SIZES the per-tier allowances
// using these constants. Keeping them apart meant the numbers and the arithmetic
// that justifies them lived in different packages.
//
// The move also unblocks the marketing site: `apps/landing` must publish the
// same allowances the API enforces, and claude-synthesis pulls in the Bedrock
// and Supabase SDKs, which a statically prerendered marketing bundle cannot
// carry. This package has no dependencies at all, so all three apps can import
// it. `claude-synthesis` re-exports every symbol below, so existing importers
// are untouched.
//
// SCOPE, AND WHAT THIS DELIBERATELY DOES NOT OWN
// ----------------------------------------------
// The per-tier NUMBERS live in `PLAN_CATALOG` (./catalog.ts) and are persisted
// per tenant in `tenant_subscriptions`, so an enterprise deal can override them.
// This module owned a competing table of its own for one commit; that was a
// mistake — two sources of truth for the same entitlement, and this one did not
// even know about the `trial` tier, so a trial tenant would have been granted
// starter-sized allowance.
//
// What lives here is only what the API, the worker, and the marketing site need
// and none should re-derive: the measured cost of a run, the warn/over/block
// thresholds, and the conversion between tokens (what is enforced) and runs
// (what a pharmacist can reason about).
//
// TOKENS ARE ENFORCED; RUNS ARE DISPLAYED. Tokens are what actually cost money
// and vary per run; a run count is what a reviewer can act on. Reporting a
// "runs remaining" figure derived from tokens would be a fabricated precision, so
// callers show the real run count alongside a percentage of the token budget.

/**
 * Tokens for one BILLED synthesis run: 4,405 input + 1,716 output.
 *
 * RE-MEASURED against `public.ai_syntheses` in prod. As of the 2026-08-29
 * snapshot that table held 87 rows across three reviews and 40 residents; this
 * is the average over the 68 of them carrying a non-zero `cost_usd`. Cache hits
 * are excluded on purpose: they write a row with `cost_usd = 0` and zero
 * tokens, so folding them in would drag the average below what a run that
 * actually costs money costs.
 *
 * Those counts describe that snapshot and nothing later — quoting them as a
 * present-tense fact is the exact mistake this re-measurement is correcting.
 * Re-run the query before trusting the figure; the constant is what matters,
 * and it is asserted in ai-allowance.test.ts.
 *
 * The previous value, 4,896 (3,748 + 1,148), cited "the prod average across 176
 * real runs" — more runs than the table has ever held at any point we can
 * observe. Whatever it described, the data does not support it, and it
 * under-stated the real run by 25%.
 *
 * Used to size plan allowances and to sanity-check that a tier covers its own
 * resident limit — NOT to predict an individual run, which varies with med-list
 * length and flag count. Output alone ranged 1,148 to 1,716 across the two
 * measurements, and `maxTokens` scales at ~180 tokens per flag, so a
 * flag-heavy resident costs multiples of this.
 */
export const TOKENS_PER_SYNTHESIS_RUN = 6_121;

/**
 * BILLED reviews per resident per month each paid tier is sized to cover, and
 * the reason `ai_token_limit_monthly` is
 * `resident_limit × TOKENS_PER_SYNTHESIS_RUN × this`.
 *
 * WAS 5, ON A FIGURE PROD DOES NOT SUPPORT. The old comment cited "193
 * syntheses across 42 distinct residents in one cycle, i.e. 4.6 per resident".
 * Grouped by review, the 2026-08-29 snapshot reads:
 *
 *   review        residents   billed runs   billed per resident
 *   e6c634b0             19            19                  1.00
 *   7b436233             29            29                  1.00
 *   9acc3fac             39            20                  0.51   (19 cache hits)
 *
 * One billed synthesis per resident per review, and rather less once the
 * response cache warms. That is the shape a MONTHLY medication regimen review
 * has by definition — the engine runs each resident once per review, it does
 * not re-run them within one.
 *
 * The 4.6 figure inflated every plan's allowance five-fold and made
 * `ltc_growth` look like it spent 74% of its revenue on inference. Measured, at
 * a realistic 80-bed census, it spends about 20%.
 *
 * 2, NOT 1. The measurement is the floor, not the ceiling: ad-hoc re-runs after
 * a med change are real and miss the cache by design, and this number is also
 * the denominator of the allowance a tenant is warned against. Doubling it
 * leaves a tenant doing ordinary work sitting at half their allowance, which is
 * the whole point — an allowance that fires the 80% warning during a normal
 * month teaches reviewers to ignore the warning.
 *
 * Exported so a tenant-facing surface can state the allotment in reviews without
 * hardcoding the multiple next to the copy that explains it.
 */
export const SYNTHESES_PER_RESIDENT_PER_MONTH = 2;

/**
 * Reviews a census is entitled to in a month. Exact — this is a plan-design
 * figure (beds × SYNTHESES_PER_RESIDENT_PER_MONTH, currently 2), not an
 * estimate derived from the token average, so it is
 * safe to show as a denominator. What is *enforced* is still the token budget:
 * a run heavier than average draws more than one fifth of a bed's share, so
 * callers must not present "allotment − used" as guaranteed remaining runs.
 */
export function monthlyRunAllotment(residents: number): number {
  if (!Number.isFinite(residents) || residents <= 0) return 0;
  return Math.ceil(residents) * SYNTHESES_PER_RESIDENT_PER_MONTH;
}

/**
 * Measured tokens for one assistant exchange: 528 input + 127 output average.
 * The input side grows with conversation history (1,856 max observed), which is
 * why a per-session turn cap exists rather than relying on this average.
 */
export const TOKENS_PER_ASSISTANT_MESSAGE = 655;

/**
 * Turns allowed in one assistant session — the cost guard that matters more than
 * the monthly count. Input tokens grow with history, so message N of a long
 * session costs multiples of message 1. Past this the client starts a fresh
 * session instead of growing one indefinitely.
 */
export const ASSISTANT_TURNS_PER_SESSION = 40;

/** Fraction of allowance at which the tenant is warned but still fully served. */
export const SOFT_WARN_FRACTION = 0.8;

/**
 * Multiple of allowance at which usage stops being overage and starts being a
 * runaway. Only this triggers a hard stop.
 *
 * Overage is served on purpose: refusing to summarise a resident carrying a
 * critical flag in order to save a few cents is the wrong trade in clinical
 * software. The ceiling catches loops and abuse, not ordinary heavy months.
 */
export const ABUSE_CEILING_MULTIPLE = 10;

export interface AllowanceState {
  used: number;
  allowance: number;
  /** 0..1 for a progress track that must not overflow. */
  fraction: number;
  /** Unclamped, so copy can honestly say "165%". */
  rawFraction: number;
  /** At or past the warn threshold — surface it, keep serving. */
  warn: boolean;
  /** Past the allowance — surface it clearly, keep serving. */
  over: boolean;
  /** Past the abuse ceiling — stop. */
  blocked: boolean;
}

/**
 * Where a tenant sits against an allowance, in whatever unit the caller passes.
 *
 * `over` and `blocked` are separate because the surface this replaces drew a full
 * green track at 165% of its cap — being over read as healthy. Callers need
 * "past the line, still working" to look different from "stopped".
 */
export function allowanceState(used: number, allowance: number): AllowanceState {
  const u = Number.isFinite(used) ? Math.max(0, used) : 0;
  // A missing or zero allowance means "not configured", not "instantly
  // exhausted" — treat it as unlimited so an unconfigured plan cannot block a
  // clinical workflow.
  if (!Number.isFinite(allowance) || allowance <= 0) {
    return { used: u, allowance: 0, fraction: 0, rawFraction: 0, warn: false, over: false, blocked: false };
  }
  const rawFraction = u / allowance;
  return {
    used: u,
    allowance,
    fraction: Math.min(1, rawFraction),
    rawFraction,
    warn: rawFraction >= SOFT_WARN_FRACTION,
    over: rawFraction >= 1,
    blocked: rawFraction >= ABUSE_CEILING_MULTIPLE,
  };
}

/**
 * Approximate synthesis runs a token budget covers. For sizing and for "about N
 * runs" copy only — never present this as an exact remaining count.
 */
export function approxRunsForTokens(tokens: number): number {
  if (!Number.isFinite(tokens) || tokens <= 0) return 0;
  return Math.floor(tokens / TOKENS_PER_SYNTHESIS_RUN);
}

/**
 * Tokens one monthly review of `residents` costs. Used to check a tier's AI
 * allowance actually covers the census that tier permits — the inconsistency
 * that left every paid tier under-provisioned before 2026-07-30.
 */
export function tokensForMonthlyReview(residents: number): number {
  if (!Number.isFinite(residents) || residents <= 0) return 0;
  return Math.ceil(residents) * TOKENS_PER_SYNTHESIS_RUN;
}
